import React from 'react';
import { GameState } from '../../types';
import { PlayerCard } from './PlayerCard';

interface GameBoardProps {
  gameState: GameState;
  currentUserId: string;
}

export const GameBoard: React.FC<GameBoardProps> = ({ gameState, currentUserId }) => {
  const currentPlayer = gameState.players.find(p => p.userId === gameState.currentTurnPlayerId);
  const isCurrentUserTurn = gameState.currentTurnPlayerId === currentUserId;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Game Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Game Board</h2>
          <div className="text-right">
            <div className="text-sm text-gray-600">Game Status</div>
            <div className={`font-semibold ${
              gameState.gameStatus === 'in-progress' ? 'text-green-600' : 'text-gray-600'
            }`}>
              {gameState.gameStatus.charAt(0).toUpperCase() + gameState.gameStatus.slice(1)}
            </div>
          </div>
        </div>

        {/* Current Turn Indicator */}
        {gameState.gameStatus === 'in-progress' && currentPlayer && (
          <div className={`p-4 rounded-lg border-2 ${
            isCurrentUserTurn 
              ? 'bg-green-50 border-green-300' 
              : 'bg-blue-50 border-blue-300'
          }`}>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold text-lg">
                  {isCurrentUserTurn ? "Your Turn!" : `${currentPlayer.username}'s Turn`}
                </div>
                <div className="text-sm text-gray-600">
                  Playing as {currentPlayer.character?.name}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">Phases Remaining</div>
                <div className="text-2xl font-bold text-blue-600">
                  {currentPlayer.stats.phasesRemaining}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Turn Order */}
      {gameState.turnOrder.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Turn Order</h3>
          <div className="flex flex-wrap gap-2">
            {gameState.turnOrder.map((playerId, index) => {
              const player = gameState.players.find(p => p.userId === playerId);
              const isCurrent = playerId === gameState.currentTurnPlayerId;
              
              return (
                <div
                  key={playerId}
                  className={`px-3 py-2 rounded-lg border ${
                    isCurrent
                      ? 'bg-blue-100 border-blue-300 text-blue-900'
                      : 'bg-gray-100 border-gray-200 text-gray-700'
                  }`}
                >
                  <div className="font-medium">
                    {index + 1}. {player?.username}
                  </div>
                  <div className="text-xs">
                    {player?.character?.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Players Grid */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-4">
          Players ({gameState.players.length})
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gameState.players.map((player) => (
            <PlayerCard
              key={player.userId}
              player={player}
              isCurrentTurn={player.userId === gameState.currentTurnPlayerId}
              isCurrentUser={player.userId === currentUserId}
            />
          ))}
        </div>
      </div>

      {/* Eliminated Players */}
      {gameState.players.some(p => p.stats.isEliminated) && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-4 text-red-600">
            Eliminated Players
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gameState.players
              .filter(p => p.stats.isEliminated)
              .map((player) => (
                <PlayerCard
                  key={player.userId}
                  player={player}
                  isCurrentTurn={false}
                  isCurrentUser={player.userId === currentUserId}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};